/**
 * Advice that points at Fact Find rows BY POSITION, re-pointed after a review
 * (Reverse Fact Find) removes or adds funds and covers (owner, Oct 2026):
 *   - recommendations / amendments .superRollovers[i]      → superannuation.currentFunds[i]
 *   - recommendations / amendments .insuranceActions[i]    → insurance.covers[i]
 *   - retained recommended covers .originalCoverIndex     → insurance.covers[i]
 *   - transferred recommended covers .transferredFromCurrentIndex and id 'transfer-N'
 *   - coverChangeExplanations / explanationLocks keys 'current_N…' and 'transfer-N…'
 *   - fundProjections.currentPortfolioByFund[i]           → currentFunds[i]
 *   - Continue/Alter contribution mirrors (active contribution rows)
 * Rows are matched by id (position only when neither row has one). Advice for
 * a removed row is dropped, as deleting a contribution already drops its
 * mirror; a row added on the review gets no advice entry (null).
 *
 * Used at AR document render (server), at Set New Baseline and on Fact Find
 * edits that remove or reorder funds and covers (browser), so
 * ONE file, CommonJS body, `.js` extension; helpers that only exist as ESM or
 * .cjs twins are passed in. No object spread (see superGuarantee.js).
 * Saved non-live advice scenarios are not touched (owner, Oct 2026).
 */

var overlay = require('./reverseFactFindOverlay');

var matchBaselineRows = overlay.matchBaselineRows;
var idKey = overlay.idKey;

/**
 * Where each baseline row ended up.
 * @returns {{ map: number[], count: number, identity: boolean, shifted: boolean }}
 *   map[baseIndex] = merged index or -1; `shifted` when a baseline row was
 *   removed or moved (rows only added at the end leave every link in place)
 */
function buildIndexRemap(baseRows, mergedRows) {
  var base = Array.isArray(baseRows) ? baseRows : [];
  var merged = Array.isArray(mergedRows) ? mergedRows : [];
  var map = base.map(function () { return -1; });
  matchBaselineRows(base, merged).forEach(function (baseIndex, mergedIndex) {
    if (baseIndex >= 0) map[baseIndex] = mergedIndex;
  });
  var shifted = !map.every(function (m, i) { return m === i; });
  var identity = base.length === merged.length && !shifted;
  return { map: map, count: merged.length, identity: identity, shifted: shifted };
}

/** Two rows share an id: links can't be matched to rows safely. */
function hasDuplicateIds(rows) {
  var seen = {};
  return (Array.isArray(rows) ? rows : []).some(function (row) {
    var key = idKey(row);
    if (!key) return false;
    if (seen[key]) return true;
    seen[key] = true;
    return false;
  });
}

function hasEntry(value) {
  return value != null;
}

/** An index-addressed array in the merged order; counts entries dropped with removed rows. */
function remapIndexedArray(list, remap, stats) {
  if (!Array.isArray(list) || !remap.shifted) return list;
  var out = [];
  for (var m = 0; m < remap.count; m += 1) out.push(null);
  list.forEach(function (entry, baseIndex) {
    var to = baseIndex < remap.map.length ? remap.map[baseIndex] : -1;
    if (to >= 0) out[to] = entry === undefined ? null : entry;
    else if (hasEntry(entry)) stats.removed += 1;
  });
  // Keep the array no longer than it needs to be (trailing gaps stay unset).
  while (out.length && out[out.length - 1] === null && out.length > list.length) out.pop();
  return out;
}

var CURRENT_KEY = /^current_(\d+)(.*)$/;
var TRANSFER_KEY = /^transfer-(\d+)(.*)$/;

/** coverChangeExplanations / explanationLocks with cover-position keys re-pointed. */
function remapCoverKeyedMap(map, remap, stats) {
  if (!map || typeof map !== 'object' || !remap.shifted) return map;
  var out = {};
  var changed = false;
  Object.keys(map).forEach(function (key) {
    var match = CURRENT_KEY.exec(key);
    var prefix = 'current_';
    if (!match) {
      match = TRANSFER_KEY.exec(key);
      prefix = 'transfer-';
    }
    if (!match) {
      out[key] = map[key];
      return;
    }
    var from = Number(match[1]);
    var to = from < remap.map.length ? remap.map[from] : -1;
    if (to < 0) {
      changed = true;
      stats.removed += 1;
      return;
    }
    var nextKey = prefix + to + match[2];
    if (nextKey !== key) changed = true;
    out[nextKey] = map[key];
  });
  return changed ? out : map;
}

function transferIndexOf(cover) {
  if (!cover) return null;
  if (cover.transferredFromCurrentIndex != null) {
    var n = Number(cover.transferredFromCurrentIndex);
    if (Number.isInteger(n)) return n;
  }
  var m = /^transfer-(\d+)$/.exec(String(cover.id || ''));
  return m ? Number(m[1]) : null;
}

/** Recommended covers with retained / transferred links re-pointed (rows for removed covers dropped). */
function remapRecommendedCovers(covers, remap, stats) {
  if (!Array.isArray(covers) || !remap.shifted) return covers;
  var changed = false;
  var out = [];
  covers.forEach(function (cover) {
    if (!cover || typeof cover !== 'object') {
      out.push(cover);
      return;
    }
    if (cover.originalCoverIndex != null && Number.isInteger(Number(cover.originalCoverIndex))) {
      var fromRetained = Number(cover.originalCoverIndex);
      var toRetained = fromRetained < remap.map.length ? remap.map[fromRetained] : -1;
      if (toRetained < 0) {
        changed = true;
        stats.removed += 1;
        return;
      }
      if (toRetained !== fromRetained) {
        changed = true;
        out.push(Object.assign({}, cover, { originalCoverIndex: toRetained }));
        return;
      }
      out.push(cover);
      return;
    }
    var fromTransfer = transferIndexOf(cover);
    if (fromTransfer != null) {
      var toTransfer = fromTransfer < remap.map.length ? remap.map[fromTransfer] : -1;
      if (toTransfer < 0) {
        changed = true;
        stats.removed += 1;
        return;
      }
      if (toTransfer !== fromTransfer) {
        changed = true;
        var patch = {};
        if (cover.transferredFromCurrentIndex != null) patch.transferredFromCurrentIndex = toTransfer;
        if (/^transfer-\d+$/.test(String(cover.id || ''))) patch.id = 'transfer-' + toTransfer;
        out.push(Object.assign({}, cover, patch));
        return;
      }
    }
    out.push(cover);
  });
  return changed ? out : covers;
}

/** The changed advice keys for one advice object (recommendations or amendments), or null. */
function remapAdviceObject(advice, fundRemap, coverRemap, coversKey, stats) {
  if (!advice || typeof advice !== 'object') return null;
  var patch = {};
  var rollovers = remapIndexedArray(advice.superRollovers, fundRemap, stats);
  if (rollovers !== advice.superRollovers) patch.superRollovers = rollovers;
  var actions = remapIndexedArray(advice.insuranceActions, coverRemap, stats);
  if (actions !== advice.insuranceActions) patch.insuranceActions = actions;
  var covers = remapRecommendedCovers(advice[coversKey], coverRemap, stats);
  if (covers !== advice[coversKey]) patch[coversKey] = covers;
  var explanations = remapCoverKeyedMap(advice.coverChangeExplanations, coverRemap, stats);
  if (explanations !== advice.coverChangeExplanations) patch.coverChangeExplanations = explanations;
  var locks = remapCoverKeyedMap(advice.explanationLocks, coverRemap, stats);
  if (locks !== advice.explanationLocks) patch.explanationLocks = locks;
  return Object.keys(patch).length ? patch : null;
}

/**
 * Re-point live Recommendations / Amendments links from the Fact Find's funds
 * and covers to the merged (reviewed) ones. A list with two rows sharing an
 * id is left alone (its links can't be matched safely).
 * @param {object} clientData — the Fact Find (with recommendations / amendments)
 * @param {object} merged — the reviewed view (buildEffectiveFormData)
 * @returns {{ recommendations: object|null, amendments: object|null, removedLinks: number,
 *   fundRemap: object, coverRemap: object }}
 */
function safeRemap(baseRows, mergedRows) {
  var remap = buildIndexRemap(baseRows, mergedRows);
  if (!remap.shifted || (!hasDuplicateIds(baseRows) && !hasDuplicateIds(mergedRows))) return remap;
  return { map: remap.map.map(function (_, i) { return i; }), count: remap.count, identity: true, shifted: false };
}

function remapReviewAdviceLinks(clientData, merged) {
  var data = clientData || {};
  var view = merged || {};
  var fundRemap = safeRemap(
    data.superannuation && data.superannuation.currentFunds,
    view.superannuation && view.superannuation.currentFunds,
  );
  var coverRemap = safeRemap(
    data.insurance && data.insurance.covers,
    view.insurance && view.insurance.covers,
  );
  var stats = { removed: 0 };
  var recommendations = remapAdviceObject(data.recommendations, fundRemap, coverRemap, 'recommendedCovers', stats);
  var amendments = remapAdviceObject(data.amendments, fundRemap, coverRemap, 'recommendedInsuranceCovers', stats);
  return {
    recommendations: recommendations,
    amendments: amendments,
    removedLinks: stats.removed,
    fundRemap: fundRemap,
    coverRemap: coverRemap,
  };
}

/**
 * Current Portfolio entries in the merged funds' order (matched like the
 * rows), so a fund deleted or added on the review doesn't hand its figures to
 * its neighbour. A fund switched to mixed options seeds its first option from
 * the fund entry, as the Fact Find's toggle does.
 * @param {(suffix: string) => object} createBlank — createBlankCurrentFundPortfolio
 */
function alignFundProjectionsToFunds(fundProjections, baseFunds, mergedFunds, createBlank) {
  var fp = fundProjections || {};
  var byFund = fp.currentPortfolioByFund;
  if (!Array.isArray(byFund) || byFund.length === 0) return fp;
  var base = Array.isArray(baseFunds) ? baseFunds : [];
  var merged = Array.isArray(mergedFunds) ? mergedFunds : [];
  var matches = matchBaselineRows(base, merged);
  var currentPortfolioByFund = merged.map(function (fund, index) {
    var entry = matches[index] >= 0 ? byFund[matches[index]] : null;
    if (!entry) return createBlank('-' + index);
    var nowMixed = Boolean(fund && fund.mixedInvestment) && !(base[matches[index]] && base[matches[index]].mixedInvestment);
    var firstOption = fund && Array.isArray(fund.investmentOptions) ? fund.investmentOptions[0] : null;
    if (!nowMixed || !firstOption || (Array.isArray(entry.options) && entry.options.length > 0)) return entry;
    var seed = JSON.parse(JSON.stringify(entry));
    delete seed.options;
    delete seed.fundId;
    // Adviser fees are fund-level (Fact Find), never per option.
    delete seed.adviserFeePercent;
    delete seed.adviserFeeCap;
    seed.optionId = firstOption.id;
    return Object.assign({}, entry, { options: [seed] });
  });
  return Object.assign({}, fp, { currentPortfolioByFund: currentPortfolioByFund });
}

/**
 * Fact Find (Oct 2026): a write that removes or reorders funds or covers
 * carries the live advice and Current Portfolio entries with their rows, as a
 * Set New Baseline does. Rows only added leave everything as it is.
 * @param {object} prevFormData — the form before the write
 * @param {object} nextFormData — the form after it
 * @param {(suffix: string) => object} createBlank — createBlankCurrentFundPortfolio
 * @returns {object} nextFormData, or a copy with `recommendations` /
 *   `amendments` / `fundProjections` re-pointed
 */
function realignAdviceToRowChanges(prevFormData, nextFormData, createBlank) {
  var prev = prevFormData || {};
  var next = nextFormData || {};
  var prevFunds = prev.superannuation && prev.superannuation.currentFunds;
  var nextFunds = next.superannuation && next.superannuation.currentFunds;
  var prevCovers = prev.insurance && prev.insurance.covers;
  var nextCovers = next.insurance && next.insurance.covers;
  if (prevFunds === nextFunds && prevCovers === nextCovers) return next;
  // The rows before the write, with the advice as it is now.
  var before = Object.assign({}, next, { superannuation: prev.superannuation, insurance: prev.insurance });
  var remap = remapReviewAdviceLinks(before, next);
  var fp = next.fundProjections;
  var alignFunds = remap.fundRemap.shifted && fp && Array.isArray(fp.currentPortfolioByFund)
    && fp.currentPortfolioByFund.length > 0 && typeof createBlank === 'function';
  if (!remap.recommendations && !remap.amendments && !alignFunds) return next;
  var out = Object.assign({}, next);
  if (remap.recommendations) out.recommendations = Object.assign({}, next.recommendations, remap.recommendations);
  if (remap.amendments) out.amendments = Object.assign({}, next.amendments, remap.amendments);
  if (alignFunds) out.fundProjections = alignFundProjectionsToFunds(fp, prevFunds, nextFunds, createBlank);
  return out;
}

/**
 * Recommendations after dropping the Continue/Alter mirrors of Fact Find
 * contribution rows the review deleted (matched by id). Highest index first,
 * so each prune sees the rows before it unchanged.
 * @param {(clientData: object, rawIndex: number) => object|null} pruneForRemoved —
 *   pruneRecommendationsForRemovedContribution (recommendedContributionMirrors)
 */
function pruneRemovedContributionMirrors(clientData, mergedItems, pruneForRemoved) {
  var data = clientData || {};
  var baseItems = data.contributions && data.contributions.items;
  if (!Array.isArray(baseItems) || baseItems.length === 0) return data.recommendations;
  var kept = {};
  (Array.isArray(mergedItems) ? mergedItems : []).forEach(function (row) {
    var k = idKey(row);
    if (k) kept[k] = true;
  });
  var working = data;
  for (var i = baseItems.length - 1; i >= 0; i -= 1) {
    var key = idKey(baseItems[i]);
    if (key && !kept[key]) {
      var prune = pruneForRemoved(working, i);
      var index = i;
      var next = Object.assign({}, working, {
        contributions: Object.assign({}, working.contributions, {
          items: working.contributions.items.filter(function (_, j) { return j !== index; }),
        }),
      });
      if (prune) next.recommendations = Object.assign({}, working.recommendations || {}, prune);
      working = next;
    }
  }
  return working.recommendations;
}

module.exports = {
  buildIndexRemap: buildIndexRemap,
  remapReviewAdviceLinks: remapReviewAdviceLinks,
  realignAdviceToRowChanges: realignAdviceToRowChanges,
  alignFundProjectionsToFunds: alignFundProjectionsToFunds,
  pruneRemovedContributionMirrors: pruneRemovedContributionMirrors,
  // For links matched by the ids the advice entries carry (adviceRowLinks.js).
  remapAdviceObject: remapAdviceObject,
  transferIndexOf: transferIndexOf,
};
